import { create } from 'zustand';
import { EditorState, ToolType, Project, Floor, Layer, Element, Point2D, BaseMap, WallElement, AreaElement, ShapeElement, DoorElement, StairElement, TextElement, Model3DElement, CameraState, RightPanelTab, Model3DPlacementState, MeasurementLine } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { getMaterialConfig, WallMaterialType } from '../utils/wallMaterialConfig';
import { getDefaultName } from '../utils/defaultNames';
import { clearImageCache } from '../components/canvas/renderers/BaseMapRenderer';
import { clearModelCache } from '../utils/model3DLoader';

interface EditorStore extends EditorState {
  // Tool actions
  setCurrentTool: (tool: ToolType) => void;

  // Selection actions
  selectElement: (id: string) => void;
  selectMultipleElements: (ids: string[]) => void;
  deselectElement: (id: string) => void;
  clearSelection: () => void;

  // Drawing actions
  startDrawing: () => void;
  addTempPoint: (point: Point2D) => void;
  updateLastTempPoint: (point: Point2D) => void;
  finishDrawing: () => void;
  cancelDrawing: () => void;

  // Element actions
  addElement: (element: Element) => void;
  updateElement: (id: string, updates: Partial<Element>) => void;
  deleteElement: (id: string) => void;
  deleteSelectedElements: () => void;

  // Layer actions
  addLayer: (name: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  deleteLayer: (id: string) => void;
  setCurrentLayer: (id: string) => void;

  // Floor actions
  addFloor: (name: string, level: number) => void;
  updateFloor: (id: string, updates: Partial<Floor>) => void;
  deleteFloor: (id: string) => void;
  setCurrentFloor: (id: string) => void;
  setDefaultFloor: (id: string) => void;

  // Project actions
  createNewProject: (name: string, baseMapInfo?: { url: string; scale: number; areaName: string; areaId: number; apiUrl?: string }) => void;
  loadProject: (project: Project, baseMapInfo?: { url: string; scale: number; areaName: string; areaId: number; apiUrl?: string }) => void;
  updateProject: (updates: Partial<Project>) => void;

  // History actions
  undo: () => void;
  redo: () => void;
  pushHistory: (isKeyAction?: boolean) => void;
  clearHistory: () => void;

  // Hover
  setHoveredElement: (id: string | null) => void;

  // Clipboard
  copySelectedElements: () => void;
  cutSelectedElements: () => void;
  pasteElements: (targetPosition?: Point2D) => void;

  // Alignment actions
  alignLeft: () => void;
  alignRight: () => void;
  alignTop: () => void;
  alignBottom: () => void;
  alignVerticalCenter: () => void;
  alignHorizontalCenter: () => void;

  // Rotation actions
  rotateElement: (elementId: string, angle: number) => void;
  rotateElementBy: (elementId: string, deltaAngle: number) => void;

  // BaseMap actions
  uploadBaseMap: (file: File) => Promise<void>;
  updateBaseMap: (updates: Partial<BaseMap>) => void;
  removeBaseMap: () => void;

  // Preview actions
  openPreview: () => void;
  closePreview: () => void;
  savePreviewCameraState: (state: CameraState) => void;
  resetPreviewCamera: () => void;

  // Wall property actions
  setWallThickness: (thickness: number) => void;
  setWallHeight: (height: number) => void;
  setWallMaterial: (material: string) => void;
  setWallColor: (color: string) => void;
  setWallOpacity: (opacity: number) => void;
  setCustomAngle: (angle: number | null) => void;
  toggleUseCustomAngle: (use: boolean) => void;

  // Right panel actions
  setRightPanelTab: (tab: RightPanelTab) => void;

  // Point hover and edit actions
  setHoveredPoint: (point: { elementId: string; pointIndex: number } | null) => void;
  updateElementPoint: (elementId: string, pointIndex: number, newPoint: Point2D) => void;

  // Area naming actions
  setPendingArea: (points: Point2D[] | null) => void;
  createAreaWithName: (name: string) => void;

  // Furniture property actions
  setPendingFurniture: (points: Point2D[] | null) => void;
  createFurnitureWithProperties: (data: { name: string; width: number; depth: number; height: number }) => void;

  // Circle property actions
  setPendingCircle: (points: Point2D[] | null) => void;
  createCircleWithProperties: (data: { name: string; radius: number; height: number }) => void;

  // Polygon property actions
  setPendingPolygon: (points: Point2D[] | null) => void;
  createPolygonWithProperties: (data: { name: string; height: number }) => void;

  // Door, Stair actions
  setDoorWindowDrawing: (data: { wallId: string; wallSegmentIndex: number; startPoint: Point2D } | null) => void;
  setPendingDoor: (data: { wallId: string; wallSegmentIndex: number; point1: Point2D; point2: Point2D } | null) => void;
  createDoorWithProperties: (data: { name?: string; height: number; openAngle: number }) => void;
  setPendingStair: (points: Point2D[] | null) => void;
  createStairWithProperties: (data: { width: number; length: number; height: number; steps: number; direction: 'up' | 'down' }) => void;

  // Text tool actions
  setPendingText: (point: Point2D | null) => void;
  createTextWithContent: (data: {
    content: string;
    fontSize?: number;
    color?: string;
    borderColor?: string;
    backgroundColor?: string;
    backgroundOpacity?: number;
  }) => void;

  // Calibration actions
  startCalibration: () => void;
  setCalibrationPoint1: (point: Point2D) => void;
  updateCalibrationPoint2: (point: Point2D) => void;
  finishCalibration: (realLengthCm?: number, imagePixelLength?: number, directModelToRealScale?: number) => void;
  cancelCalibration: () => void;
  // 自动校准（根据已知比例直接计算）
  autoCalibrate: (scale: number) => void;

  // 3D Model placement actions
  startModel3DPlacement: (model: Model3DPlacementState) => void;
  cancelModel3DPlacement: () => void;
  placeModel3D: (position: Point2D, outlineData?: { lines: { x: number; y: number }[][]; width: number; height: number }) => void;

  // Category visibility actions
  setHiddenCategories: (categories: Record<string, boolean>) => void;
  toggleCategoryVisibility: (category: string) => void;

  // Measurement tool actions
  startMeasurement: () => void;
  addMeasurementPoint: (point: Point2D) => void;
  updateMeasurementPreview: (point: Point2D | null) => void;
  completeMeasurement: () => void;
  cancelMeasurement: () => void;
  deleteMeasurement: (id: string) => void;
  clearAllMeasurements: () => void;

  // External API actions
  setApiUrl: (apiUrl: string | null) => void;

  // Guided Tour actions
  setGuidedTourEnabled: (enabled: boolean) => void;
  setGuidedTourStep: (step: number) => void;
  setGuidedTourCompleted: (completed: boolean) => void;
  setGuidedTourDismissed: (dismissed: boolean) => void;
  resetGuidedTour: () => void;
  setBaseMapCentered: (centered: boolean) => void;

  // 项目加载版本号（用于触发 Canvas 重新挂载）
  // 只在加载/创建新项目时递增，不在元素修改时递增
  projectLoadVersion: number;
}

const createDefaultProject = (name: string): Project => {
  const floorId = uuidv4();
  const layerId = uuidv4();

  return {
    id: uuidv4(),
    name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    mapConfig: {
      center: { x: 0, y: 0 },
      zoom: 1,
      rotation: 0,
    },
    floors: [
      {
        id: floorId,
        name: '1F',
        level: 1,
        height: 3000, // 3 meters in mm
        elevation: 0,
        visible: true,
        layers: [
          {
            id: layerId,
            name: '__default_layer__',
            visible: true,
            locked: false,
            color: '#1e5a8a',
            elements: [],
          },
        ],
      },
    ],
    metadata: {},
  };
};

export const useEditorStore = create<EditorStore>((set, get) => ({
  // Initial state
  currentTool: ToolType.SELECT,
  selectedElements: [],
  currentFloor: '',
  currentLayer: '',
  project: null,
  isDrawing: false,
  tempPoints: [],
  hoveredElement: null,
  clipboard: [],
  history: {
    past: [],
    future: [],
  },
  isPreviewOpen: false,
  previewCameraState: null,
  wallProperties: {
    thickness: 200,  // 默认墙厚200mm
    height: 375,     // 3D渲染高度375mm（逻辑高度3000mm即3米）
    customAngle: null,  // 自定义角度（度）
    useCustomAngle: false,  // 是否使用自定义角度
    material: 'concrete',  // 默认材质：混凝土
    color: '#a8a29e',      // 混凝土默认颜色
    opacity: 1,            // 默认完全不透明
  },
  rightPanelTab: 'layers',
  hoveredPoint: null,
  pendingArea: null,
  pendingFurniture: null,
  pendingCircle: null,
  pendingPolygon: null,
  pendingDoor: null,
  doorWindowDrawing: null,
  pendingStair: null,
  pendingText: null,
  hiddenCategories: {},
  // Calibration state
  isCalibrating: false,
  calibrationPoint1: null,
  calibrationPoint2: null,
  // 底图加载状态
  isBaseMapLoading: false,
  // 3D Model placement state
  model3DPlacement: null,
  // Measurement tool state
  isMeasuring: false,
  currentMeasurement: null,
  completedMeasurements: [],
  measurementPreviewPoint: null,

  // External API state
  apiUrl: null,

  // Guided Tour state
  isGuidedTourEnabled: false,
  guidedTourStep: 0,
  guidedTourCompleted: false,
  guidedTourDismissed: false,
  baseMapCentered: false,
  // 项目加载版本号
  projectLoadVersion: 0,

  // Guided Tour actions
  setGuidedTourEnabled: (enabled: boolean) => set({ isGuidedTourEnabled: enabled }),
  setGuidedTourStep: (step: number) => set({ guidedTourStep: step }),
  setGuidedTourCompleted: (completed: boolean) => set({ guidedTourCompleted: completed }),
  setGuidedTourDismissed: (dismissed: boolean) => set({ guidedTourDismissed: dismissed }),
  resetGuidedTour: () => set({ 
    isGuidedTourEnabled: false, 
    guidedTourStep: 0, 
    guidedTourCompleted: false, 
    guidedTourDismissed: false,
    baseMapCentered: false,
  }),
  setBaseMapCentered: (centered: boolean) => set({ baseMapCentered: centered }),

  // Tool actions
  setCurrentTool: (tool) => {
    set({ currentTool: tool, isDrawing: false, tempPoints: [], doorWindowDrawing: null });

    // 如果切换到测量工具，自动开始测量
    if (tool === ToolType.MEASURE) {
      get().startMeasurement();
    } else {
      // 切换到其他工具时，清除所有测量线
      set({
        isMeasuring: false,
        currentMeasurement: null,
        measurementPreviewPoint: null,
        completedMeasurements: [], // 清除所有已完成的测量线
      });
    }
  },

  // Selection actions
  selectElement: (id) => set({ selectedElements: [id] }),
  selectMultipleElements: (ids) => set({ selectedElements: ids }),
  deselectElement: (id) =>
    set((state) => ({
      selectedElements: state.selectedElements.filter((eid) => eid !== id),
    })),
  clearSelection: () => set({ selectedElements: [] }),

  // Drawing actions
  startDrawing: () => set({ isDrawing: true, tempPoints: [] }),
  addTempPoint: (point) =>
    set((state) => ({
      isDrawing: true,
      tempPoints: [...state.tempPoints, point],
    })),
  updateLastTempPoint: (point) =>
    set((state) => {
      if (state.tempPoints.length === 0) return state;
      const newPoints = [...state.tempPoints];
      newPoints[newPoints.length - 1] = point;
      return { tempPoints: newPoints };
    }),
  finishDrawing: () => {
    const state = get();
    if (state.tempPoints.length < 2) {
      set({ isDrawing: false, tempPoints: [] });
      return;
    }

    // 特殊处理区域工具：需要打开命名面板并设置validArea
    if (state.currentTool === ToolType.AREA && state.tempPoints.length >= 3) {
      get().setPendingArea(state.tempPoints);
      return;
    }

    // 特殊处理多边形工具：需要打开属性面板
    if (state.currentTool === ToolType.POLYGON && state.tempPoints.length >= 3) {
      get().setPendingPolygon(state.tempPoints);
      return;
    }

    // 其他工具直接创建元素
    const newElement = createElementFromPoints(
      state.currentTool,
      state.tempPoints,
      state.currentLayer,
      state.currentFloor,
      state.wallProperties
    );

    if (newElement) {
      get().addElement(newElement);
    }

    set({ isDrawing: false, tempPoints: [] });
  },
  cancelDrawing: () => set({ isDrawing: false, tempPoints: [] }),

  // Element actions
  addElement: (element) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    const floor = project.floors.find((f) => f.id === state.currentFloor);
    if (!floor) return;

    const layer = floor.layers.find((l) => l.id === state.currentLayer);
    if (!layer) return;

    layer.elements.push(element);
    project.updatedAt = new Date().toISOString();

    set({ project });
    get().pushHistory(true); // 添加元素是关键操作
  },

  updateElement: (id, updates) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    let updated = false;

    for (const floor of project.floors) {
      for (const layer of floor.layers) {
        const elementIndex = layer.elements.findIndex((e) => e.id === id);
        if (elementIndex !== -1) {
          const element = layer.elements[elementIndex];

          // 特殊处理：Model3D元素的realWidth/realDepth更新时，重新计算canvas尺寸
          if (element.type === 'model3d' && ('realWidth' in updates || 'realDepth' in updates)) {
            const calibrationScale = project.mapConfig?.calibrationScale || 10; // 默认10px/cm
            const isCalibrated = project.mapConfig?.isCalibrated || false;
            const isAutoCalibrated = project.mapConfig?.autoCalibrated || false;

            // 获取新的realWidth和realDepth
            // 如果旧元素没有这些字段，从width/depth反推
            let newRealWidth = (updates as any).realWidth;
            let newRealDepth = (updates as any).realDepth;

            if (!newRealWidth && !(element as any).realWidth) {
              // 旧元素没有realWidth，从width反推
              if (isAutoCalibrated) {
                newRealWidth = ((element as any).width || 50) * 10; // autoCalibrated: width直接是cm
              } else {
                newRealWidth = ((element as any).width / calibrationScale) * 10; // px → cm → mm
              }
              console.warn('⚠️ 旧元素缺少realWidth，从width反推:', newRealWidth, 'mm');
            } else if (!newRealWidth) {
              newRealWidth = (element as any).realWidth;
            }

            if (!newRealDepth && !(element as any).realDepth) {
              // 旧元素没有realDepth，从depth反推
              if (isAutoCalibrated) {
                newRealDepth = ((element as any).depth || 50) * 10; // autoCalibrated: depth直接是cm
              } else {
                newRealDepth = ((element as any).depth / calibrationScale) * 10; // px → cm → mm
              }
              console.warn('⚠️ 旧元素缺少realDepth，从depth反推:', newRealDepth, 'mm');
            } else if (!newRealDepth) {
              newRealDepth = (element as any).realDepth;
            }

            // 重新计算canvas尺寸
            // 自动校准：世界坐标直接是cm，canvasWidth = cm值
            // 手动校准：canvasWidth = cm * calibrationScale
            let newCanvasWidth: number;
            let newCanvasDepth: number;

            if (isAutoCalibrated) {
              newCanvasWidth = newRealWidth / 10; // mm → cm（直接作为世界坐标）
              newCanvasDepth = newRealDepth / 10; // mm → cm
            } else {
              newCanvasWidth = (newRealWidth / 10) * calibrationScale; // mm → cm → px
              newCanvasDepth = (newRealDepth / 10) * calibrationScale; // mm → cm → px
            }

            console.log('🔄 更新Model3D尺寸:', {
              realSize: { width: newRealWidth, depth: newRealDepth, unit: 'mm' },
              calibrationScale: calibrationScale.toFixed(4) + ' px/cm',
              isAutoCalibrated,
              canvasSize: { width: newCanvasWidth.toFixed(2), depth: newCanvasDepth.toFixed(2), unit: 'px' },
              isCalibrated
            });

            // 合并更新，包括重新计算的canvas尺寸
            updates = {
              ...updates,
              width: newCanvasWidth,
              depth: newCanvasDepth,
            };

            // 重新计算points（保持中心位置不变，同时考虑旋转角度）
            const oldElement = element as any;
            const centerX = oldElement.points.reduce((sum: number, p: Point2D) => sum + p.x, 0) / oldElement.points.length;
            const centerY = oldElement.points.reduce((sum: number, p: Point2D) => sum + p.y, 0) / oldElement.points.length;

            console.log(`📐 [updateElement Model3D] 元素: ${oldElement.name || oldElement.id}`);
            console.log(`📐 [updateElement Model3D] 当前旋转角度: ${oldElement.rotation || 0}°`);
            console.log(`📐 [updateElement Model3D] 中心点: (${centerX.toFixed(2)}, ${centerY.toFixed(2)})`);
            console.log(`📐 [updateElement Model3D] 当前points:`, oldElement.points.map((p: Point2D, i: number) => `[${i}](${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));
            console.log(`📐 [updateElement Model3D] 新尺寸: ${newCanvasWidth.toFixed(2)} x ${newCanvasDepth.toFixed(2)}`);

            // 获取当前旋转角度（弧度）
            const rotationRad = (oldElement.rotation || 0) * Math.PI / 180;
            const cos = Math.cos(rotationRad);
            const sin = Math.sin(rotationRad);
            console.log(`📐 [updateElement Model3D] 旋转弧度: ${rotationRad.toFixed(4)}, cos: ${cos.toFixed(4)}, sin: ${sin.toFixed(4)}`);

            const halfWidth = newCanvasWidth / 2;
            const halfDepth = newCanvasDepth / 2;

            // 相对于中心的偏移量
            const offsets = [
              { dx: -halfWidth, dy: -halfDepth },  // 左上
              { dx: halfWidth, dy: -halfDepth },   // 右上
              { dx: halfWidth, dy: halfDepth },    // 右下
              { dx: -halfWidth, dy: halfDepth },   // 左下
            ];

            // 应用旋转公式计算新角点
            const newPoints = offsets.map((offset, i) => {
              const newX = centerX + (offset.dx * cos - offset.dy * sin);
              const newY = centerY + (offset.dx * sin + offset.dy * cos);
              console.log(`📐 [updateElement Model3D] 角点${i}: offset(${offset.dx.toFixed(1)}, ${offset.dy.toFixed(1)}) → (${newX.toFixed(1)}, ${newY.toFixed(1)})`);
              return { x: newX, y: newY };
            });

            // 验证新中心点
            const newCenterX = newPoints.reduce((sum: number, p: Point2D) => sum + p.x, 0) / newPoints.length;
            const newCenterY = newPoints.reduce((sum: number, p: Point2D) => sum + p.y, 0) / newPoints.length;
            console.log(`📐 [updateElement Model3D] 新中心点: (${newCenterX.toFixed(2)}, ${newCenterY.toFixed(2)})`);
            console.log(`📐 [updateElement Model3D] 中心点偏移: (${(newCenterX - centerX).toFixed(4)}, ${(newCenterY - centerY).toFixed(4)})`);

            (updates as any).points = newPoints;
          }

          // 特殊处理：家具的width/depth更新时重新计算points（厘米转米）
          if (
            element.type === 'rectangle' &&
            (element as ShapeElement).furnitureName &&
            ('width' in updates || 'depth' in updates)
          ) {
            const oldElement = element as ShapeElement;
            const newWidthCm = (updates as any).width ?? oldElement.width;
            const newDepthCm = (updates as any).depth ?? oldElement.depth;
            const newWidthM = newWidthCm / 100;
            const newDepthM = newDepthCm / 100;
            const centerX = oldElement.points.reduce((sum, p) => sum + p.x, 0) / oldElement.points.length;
            const centerY = oldElement.points.reduce((sum, p) => sum + p.y, 0) / oldElement.points.length;
            const rotationRad = (oldElement.rotation || 0) * Math.PI / 180;
            const cos = Math.cos(rotationRad);
            const sin = Math.sin(rotationRad);
            const halfWidth = newWidthM / 2;
            const halfDepth = newDepthM / 2;
            const offsets = [
              { dx: -halfWidth, dy: -halfDepth },
              { dx: halfWidth, dy: -halfDepth },
              { dx: halfWidth, dy: halfDepth },
              { dx: -halfWidth, dy: halfDepth },
            ];
            const newPoints = offsets.map((offset) => ({
              x: centerX + (offset.dx * cos - offset.dy * sin),
              y: centerY + (offset.dx * sin + offset.dy * cos),
            }));
            updates = { ...updates, width: newWidthCm, depth: newDepthCm, points: newPoints };
          }

          layer.elements[elementIndex] = {
            ...layer.elements[elementIndex],
            ...updates,
          } as Element;
          updated = true;
          break;
        }
      }
      if (updated) break;
    }

    if (updated) {
      project.updatedAt = new Date().toISOString();
      set({ project });
      get().pushHistory();
    }
  },

  deleteElement: (id) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    let deleted = false;

    for (const floor of project.floors) {
      for (const layer of floor.layers) {
        const elementIndex = layer.elements.findIndex((e) => e.id === id);
        if (elementIndex !== -1) {
          layer.elements.splice(elementIndex, 1);
          deleted = true;
          break;
        }
      }
      if (deleted) break;
    }

    if (deleted) {
      project.updatedAt = new Date().toISOString();
      set({
        project,
        selectedElements: state.selectedElements.filter((eid) => eid !== id),
      });
      get().pushHistory(true); // 删除元素是关键操作
    }
  },

  deleteSelectedElements: () => {
    const state = get();
    state.selectedElements.forEach((id) => get().deleteElement(id));
    set({ selectedElements: [] });
  },

  // Layer actions
  addLayer: (name) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    const floor = project.floors.find((f) => f.id === state.currentFloor);
    if (!floor) return;

    const newLayer: Layer = {
      id: uuidv4(),
      name,
      visible: true,
      locked: false,
      color: '#1e5a8a',
      elements: [],
    };

    floor.layers.push(newLayer);
    project.updatedAt = new Date().toISOString();

    set({ project, currentLayer: newLayer.id });
    get().pushHistory(true); // 添加图层是关键操作
  },

  updateLayer: (id, updates) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    let updated = false;

    for (const floor of project.floors) {
      const layerIndex = floor.layers.findIndex((l) => l.id === id);
      if (layerIndex !== -1) {
        floor.layers[layerIndex] = {
          ...floor.layers[layerIndex],
          ...updates,
        };
        updated = true;
        break;
      }
    }

    if (updated) {
      project.updatedAt = new Date().toISOString();
      set({ project });
      get().pushHistory(true); // 删除图层是关键操作
    }
  },

  deleteLayer: (id) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    let deleted = false;

    for (const floor of project.floors) {
      if (floor.layers.length <= 1) continue; // Keep at least one layer

      const layerIndex = floor.layers.findIndex((l) => l.id === id);
      if (layerIndex !== -1) {
        floor.layers.splice(layerIndex, 1);
        deleted = true;

        // Switch to first available layer
        if (state.currentLayer === id) {
          set({ currentLayer: floor.layers[0].id });
        }
        break;
      }
    }

    if (deleted) {
      project.updatedAt = new Date().toISOString();
      set({ project });
      get().pushHistory(true); // 删除图层是关键操作
    }
  },

  setCurrentLayer: (id) => set({ currentLayer: id }),

  // Floor actions
  addFloor: (name, level) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    const layerId = uuidv4();

    const newFloor: Floor = {
      id: uuidv4(),
      name,
      level,
      height: 3000,
      elevation: level * 3000,
      visible: true,
      layers: [
        {
          id: layerId,
          name: '__default_layer__',
          visible: true,
          locked: false,
          color: '#1e5a8a',
          elements: [],
        },
      ],
    };

    project.floors.push(newFloor);
    project.floors.sort((a, b) => a.level - b.level);
    project.updatedAt = new Date().toISOString();

    set({ project, currentFloor: newFloor.id, currentLayer: layerId });
    get().pushHistory(true); // 添加楼层是关键操作
  },

  updateFloor: (id, updates) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    const floorIndex = project.floors.findIndex((f) => f.id === id);

    if (floorIndex !== -1) {
      project.floors[floorIndex] = {
        ...project.floors[floorIndex],
        ...updates,
      };
      project.updatedAt = new Date().toISOString();
      set({ project });
      get().pushHistory();
    }
  },

  deleteFloor: (id) => {
    const state = get();
    if (!state.project || state.project.floors.length <= 1) return;

    const project = { ...state.project };
    const floorIndex = project.floors.findIndex((f) => f.id === id);

    if (floorIndex !== -1) {
      project.floors.splice(floorIndex, 1);
      project.updatedAt = new Date().toISOString();

      // Switch to first available floor
      if (state.currentFloor === id) {
        const newFloor = project.floors[0];
        set({
          project,
          currentFloor: newFloor.id,
          currentLayer: newFloor.layers[0].id,
        });
      } else {
        set({ project });
      }

      get().pushHistory();
    }
  },

  setDefaultFloor: (id) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };

    // 清除所有楼层的isDefault标记
    project.floors.forEach((floor: any) => {
      floor.isDefault = false;
    });

    // 设置指定楼层为默认
    const targetFloor = project.floors.find((f) => f.id === id);
    if (targetFloor) {
      (targetFloor as any).isDefault = true;
      console.log('已设置默认楼层:', targetFloor.name);
    }

    project.updatedAt = new Date().toISOString();
    set({ project });
    get().pushHistory();
  },

  setCurrentFloor: (id) => {
    const state = get();
    if (!state.project) return;

    const floor = state.project.floors.find((f) => f.id === id);
    if (floor) {
      set({
        currentFloor: id,
        currentLayer: floor.layers[0].id,
        selectedElements: [],
      });
    }
  },

  // Project actions
  createNewProject: (name, baseMapInfo?) => {
    // 清理画布缓存
    clearImageCache();
    clearModelCache();

    const project = createDefaultProject(name);
    
    // 如果有 apiUrl，设置到 store
    if (baseMapInfo?.apiUrl) {
      set({ apiUrl: baseMapInfo.apiUrl });
    }
    
    set({
      project,
      currentFloor: project.floors[0].id,
      currentLayer: project.floors[0].layers[0].id,
      selectedElements: [],
      history: { past: [], future: [] },
      isBaseMapLoading: false,
      baseMapCentered: false,
      // 递增项目加载版本号，触发 Canvas 重新挂载
      projectLoadVersion: (get().projectLoadVersion || 0) + 1,
    });

    // 检查是否有待导入的外部底图
    const pendingBaseMapStr = sessionStorage.getItem('pendingBaseMap') || (baseMapInfo ? JSON.stringify(baseMapInfo) : null);
    if (pendingBaseMapStr) {
      const pendingBaseMap = JSON.parse(pendingBaseMapStr);

      console.log('🗺️ createNewProject 开始加载外部底图:', pendingBaseMap.url);

      // 设置加载状态
      set({ isBaseMapLoading: true });

      // 下载底图
      const img = new Image();

      img.onload = () => {
        console.log('✅ 图片加载成功，尺寸:', img.width, 'x', img.height);

        // 设置底图
        // scale 固定为 100，与手动上传一致，原始 scale 通过 finishCalibration 传入
        const baseMap: BaseMap = {
          id: uuidv4(),
          fileName: pendingBaseMap.areaName || 'external-map',
          imageData: pendingBaseMap.url,
          width: img.width,
          height: img.height,
          scale: 100,
          offset: { x: 0, y: 0 },
          rotation: 0,
          opacity: 0.7,
          visible: true,
        };

        // 保存底图到项目
        const state = get();
        if (state.project) {
          set({
            project: {
              ...state.project,
              mapConfig: {
                ...state.project.mapConfig,
                baseMap,
              },
            },
          });

          // 直接调用 finishCalibration，传入 res_url 中的 scale 作为 modelToRealScale
          const scale = pendingBaseMap.scale;
          get().finishCalibration(undefined, undefined, scale);
        }

        set({ isBaseMapLoading: false });
        console.log('✅ 自动导入底图完成');
        sessionStorage.removeItem('pendingBaseMap');
      };

      img.onerror = (e) => {
        console.error('❌ 图片加载失败:', pendingBaseMap.url, e);
        set({ isBaseMapLoading: false });
      };

      img.src = pendingBaseMap.url;
    }
  },

  loadProject: (project, baseMapInfo?: { url: string; scale: number; areaName: string; areaId: number; apiUrl?: string }) => {
    // 清理画布缓存
    clearImageCache();
    clearModelCache();

    // 如果有 apiUrl，设置到 store
    if (baseMapInfo?.apiUrl) {
      set({ apiUrl: baseMapInfo.apiUrl });
    }
    
    set({
      project,
      currentFloor: project.floors[0].id,
      currentLayer: project.floors[0].layers[0].id,
      selectedElements: [],
      history: { past: [], future: [] },
      isBaseMapLoading: false,
      baseMapCentered: false,
      // 递增项目加载版本号，触发 Canvas 重新挂载
      projectLoadVersion: (get().projectLoadVersion || 0) + 1,
    });

    // 检查是否有待导入的外部底图
    const pendingBaseMapStr = sessionStorage.getItem('pendingBaseMap') || (baseMapInfo ? JSON.stringify(baseMapInfo) : null);
    if (pendingBaseMapStr) {
      const pendingBaseMap = JSON.parse(pendingBaseMapStr);

      console.log('🗺️ 开始加载外部底图:', pendingBaseMap.url);

      // 设置加载状态
      set({ isBaseMapLoading: true });

      // 下载底图
      const img = new Image();

      img.onload = () => {
        console.log('✅ 图片加载成功，尺寸:', img.width, 'x', img.height);

        // 设置底图
        // scale 固定为 100，与手动上传一致，原始 scale 通过 finishCalibration 传入
        const baseMap: BaseMap = {
          id: uuidv4(),
          fileName: pendingBaseMap.areaName || 'external-map',
          imageData: pendingBaseMap.url,
          width: img.width,
          height: img.height,
          scale: 100,
          offset: { x: 0, y: 0 },
          rotation: 0,
          opacity: 0.7,
          visible: true,
        };

        // 保存底图到项目
        const state = get();
        if (state.project) {
          set({
            project: {
              ...state.project,
              mapConfig: {
                ...state.project.mapConfig,
                baseMap,
              },
            },
          });

          // 直接调用 finishCalibration，传入 res_url 中的 scale 作为 modelToRealScale
          const scale = pendingBaseMap.scale || 0;
          get().finishCalibration(undefined, undefined, scale);
        }

        set({ isBaseMapLoading: false });
        console.log('✅ 自动导入底图完成');
        sessionStorage.removeItem('pendingBaseMap');
      };

      img.onerror = (e) => {
        console.error('❌ 图片加载失败:', e);
        console.error('   图片URL:', pendingBaseMap.url);
        console.error('   提示: 检查图片URL是否可访问，或是否有跨域问题');
        set({ isBaseMapLoading: false });
      };

      img.src = pendingBaseMap.url;
    }
  },

  updateProject: (updates) => {
    const state = get();
    if (!state.project) return;

    const project = {
      ...state.project,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    set({ project });
    get().pushHistory();
  },

  // History actions
  clearHistory: () => {
    set({
      history: {
        past: [],
        future: [],
      },
    });
    console.log('🗑️ 历史记录已清空');
  },

  pushHistory: (isKeyAction = false) => {
    const state = get();
    if (!state.project) return;

    // 限制历史记录数量，最多保留5步（减少内存占用）
    const MAX_HISTORY = 5;

    const now = Date.now();
    const lastHistoryTime = (state as any).lastHistoryTime || 0;

    // 关键操作立即记录，非关键操作使用防抖
    if (!isKeyAction) {
      // 防抖：如果距离上次记录时间小于500ms，跳过
      // 这样可以避免连续操作（如键盘移动）产生大量历史记录
      const DEBOUNCE_TIME = 500; // 500ms防抖

      if (now - lastHistoryTime < DEBOUNCE_TIME) {
        // 更新时间戳但不记录历史
        (state as any).lastHistoryTime = now;
        return;
      }
    }

    const past = [...state.history.past, JSON.parse(JSON.stringify(state.project))];

    // 如果超过限制，删除最旧的记录
    if (past.length > MAX_HISTORY) {
      past.shift(); // 删除第一个（最旧的）
    }

    set({
      history: {
        past,
        future: [],
      },
      lastHistoryTime: now,
    } as any);
  },

  undo: () => {
    const state = get();
    if (state.history.past.length === 0) return;

    const past = [...state.history.past];
    const previous = past.pop();

    if (previous && state.project) {
      set({
        project: previous,
        history: {
          past,
          future: [state.project, ...state.history.future],
        },
      });
    }
  },

  redo: () => {
    const state = get();
    if (state.history.future.length === 0) return;

    const future = [...state.history.future];
    const next = future.shift();

    if (next && state.project) {
      set({
        project: next,
        history: {
          past: [...state.history.past, state.project],
          future,
        },
      });
    }
  },

  // Hover
  setHoveredElement: (id) => set({ hoveredElement: id }),

  // Clipboard
  copySelectedElements: () => {
    const state = get();
    if (!state.project || state.selectedElements.length === 0) return;

    const elements: Element[] = [];

    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            elements.push(JSON.parse(JSON.stringify(element)));
          }
        }
      }
    }

    set({ clipboard: elements });
  },

  pasteElements: (targetPosition?: Point2D) => {
    const state = get();
    if (state.clipboard.length === 0) return;

    // 计算剪贴板中所有元素的中心点
    let centerX = 0;
    let centerY = 0;
    let totalPoints = 0;

    state.clipboard.forEach((element) => {
      element.points.forEach((p) => {
        centerX += p.x;
        centerY += p.y;
        totalPoints++;
      });
    });

    if (totalPoints > 0) {
      centerX /= totalPoints;
      centerY /= totalPoints;
    }

    // 计算偏移量
    let offsetX: number;
    let offsetY: number;

    if (targetPosition) {
      // 如果提供了目标位置，将元素中心移动到目标位置
      offsetX = targetPosition.x - centerX;
      offsetY = targetPosition.y - centerY;
    } else {
      // 否则使用默认偏移（向右下移动100像素）
      offsetX = 100;
      offsetY = 100;
    }

    const newElements = state.clipboard.map((element) => {
      // 生成以"kimap"开头的唯一ID
      const uuid = uuidv4().replace(/-/g, '');
      return {
        ...element,
        id: `kimap${uuid}`,
        points: element.points.map((p) => ({ x: p.x + offsetX, y: p.y + offsetY })),
        layerId: state.currentLayer,
        floorId: state.currentFloor,
      };
    });

    newElements.forEach((element) => get().addElement(element));
    set({ selectedElements: newElements.map((e) => e.id) });
  },

  cutSelectedElements: () => {
    const state = get();
    if (!state.project || state.selectedElements.length === 0) return;

    // 先复制
    get().copySelectedElements();

    // 再删除
    get().deleteSelectedElements();
  },

  // Alignment actions
  alignLeft: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    // 获取所有选中元素
    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 找到最左边的x坐标
    let minX = Infinity;
    for (const element of selectedElementObjs) {
      for (const point of element.points) {
        if (point.x < minX) minX = point.x;
      }
    }

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementMinX = Math.min(...element.points.map(p => p.x));
      const offset = minX - elementMinX;

      const newPoints = element.points.map(p => ({ x: p.x + offset, y: p.y }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  alignRight: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 找到最右边的x坐标
    let maxX = -Infinity;
    for (const element of selectedElementObjs) {
      for (const point of element.points) {
        if (point.x > maxX) maxX = point.x;
      }
    }

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementMaxX = Math.max(...element.points.map(p => p.x));
      const offset = maxX - elementMaxX;

      const newPoints = element.points.map(p => ({ x: p.x + offset, y: p.y }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  alignTop: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 找到最上边的y坐标
    let minY = Infinity;
    for (const element of selectedElementObjs) {
      for (const point of element.points) {
        if (point.y < minY) minY = point.y;
      }
    }

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementMinY = Math.min(...element.points.map(p => p.y));
      const offset = minY - elementMinY;

      const newPoints = element.points.map(p => ({ x: p.x, y: p.y + offset }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  alignBottom: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 找到最下边的y坐标
    let maxY = -Infinity;
    for (const element of selectedElementObjs) {
      for (const point of element.points) {
        if (point.y > maxY) maxY = point.y;
      }
    }

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementMaxY = Math.max(...element.points.map(p => p.y));
      const offset = maxY - elementMaxY;

      const newPoints = element.points.map(p => ({ x: p.x, y: p.y + offset }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  alignVerticalCenter: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 计算所有元素的平均中心y坐标
    let totalCenterY = 0;
    for (const element of selectedElementObjs) {
      const centerY = element.points.reduce((sum, p) => sum + p.y, 0) / element.points.length;
      totalCenterY += centerY;
    }
    const avgCenterY = totalCenterY / selectedElementObjs.length;

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementCenterY = element.points.reduce((sum, p) => sum + p.y, 0) / element.points.length;
      const offset = avgCenterY - elementCenterY;

      const newPoints = element.points.map(p => ({ x: p.x, y: p.y + offset }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  alignHorizontalCenter: () => {
    const state = get();
    if (!state.project || state.selectedElements.length < 2) return;

    const selectedElementObjs: Element[] = [];
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        for (const element of layer.elements) {
          if (state.selectedElements.includes(element.id)) {
            selectedElementObjs.push(element);
          }
        }
      }
    }

    if (selectedElementObjs.length < 2) return;

    // 计算所有元素的平均中心x坐标
    let totalCenterX = 0;
    for (const element of selectedElementObjs) {
      const centerX = element.points.reduce((sum, p) => sum + p.x, 0) / element.points.length;
      totalCenterX += centerX;
    }
    const avgCenterX = totalCenterX / selectedElementObjs.length;

    // 对齐所有元素
    for (const element of selectedElementObjs) {
      const elementCenterX = element.points.reduce((sum, p) => sum + p.x, 0) / element.points.length;
      const offset = avgCenterX - elementCenterX;

      const newPoints = element.points.map(p => ({ x: p.x + offset, y: p.y }));
      get().updateElement(element.id, { points: newPoints });
    }

    get().pushHistory();
  },

  // Rotation actions
  rotateElement: (elementId, angle) => {
    const state = get();
    if (!state.project) return;

    // 查找元素
    let element: Element | null = null;
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        const found = layer.elements.find(el => el.id === elementId);
        if (found) {
          element = found;
          break;
        }
      }
      if (element) break;
    }

    if (!element || element.points.length === 0) return;

    // 计算元素中心点
    const centerX = element.points.reduce((sum, p) => sum + p.x, 0) / element.points.length;
    const centerY = element.points.reduce((sum, p) => sum + p.y, 0) / element.points.length;

    // 计算当前旋转角度（如果有的话）
    const currentRotation = element.rotation || 0;
    const deltaAngle = angle - currentRotation;

    console.log(`🔄 [rotateElement] 元素: ${element.name || elementId}`);
    console.log(`🔄 [rotateElement] 当前角度: ${currentRotation}° → 新角度: ${angle}°, 差值: ${deltaAngle}°`);
    console.log(`🔄 [rotateElement] 中心点: (${centerX.toFixed(2)}, ${centerY.toFixed(2)})`);
    console.log(`🔄 [rotateElement] 原始points:`, element.points.map((p, i) => `[${i}](${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));

    // 将角度转换为弧度
    const radians = (deltaAngle * Math.PI) / 180;

    // 旋转所有点
    console.log(`🔄 [rotateElement] ==================== 旋转计算详情 ====================`);
    console.log(`🔄 [rotateElement] 角度差: ${deltaAngle}° = ${radians.toFixed(6)} rad`);
    console.log(`🔄 [rotateElement] cos(δ)=${Math.cos(radians).toFixed(6)}, sin(δ)=${Math.sin(radians).toFixed(6)}`);

    const newPoints = element.points.map((p, i) => {
      const dx = p.x - centerX;
      const dy = p.y - centerY;

      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      const newX = centerX + (dx * cos - dy * sin);
      const newY = centerY + (dx * sin + dy * cos);

      console.log(`🔄 [rotateElement] P${i}: (${p.x.toFixed(2)}, ${p.y.toFixed(2)}) → rel(${dx.toFixed(2)}, ${dy.toFixed(2)}) → new(${newX.toFixed(2)}, ${newY.toFixed(2)})`);

      return {
        x: newX,
        y: newY,
      };
    });

    console.log(`🔄 [rotateElement] 旋转后points:`, newPoints.map((p, i) => `[${i}](${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));

    // 验证新中心点
    const newCenterX = newPoints.reduce((sum, p) => sum + p.x, 0) / newPoints.length;
    const newCenterY = newPoints.reduce((sum, p) => sum + p.y, 0) / newPoints.length;
    console.log(`🔄 [rotateElement] 旋转后中心点: (${newCenterX.toFixed(2)}, ${newCenterY.toFixed(2)})`);
    console.log(`🔄 [rotateElement] 中心点偏移: (${(newCenterX - centerX).toFixed(4)}, ${(newCenterY - centerY).toFixed(4)})`);

    // 更新元素
    get().updateElement(elementId, {
      points: newPoints,
      rotation: angle
    });
    get().pushHistory();
  },

  rotateElementBy: (elementId, deltaAngle) => {
    const state = get();
    if (!state.project) return;

    // 查找元素
    let element: Element | null = null;
    for (const floor of state.project.floors) {
      for (const layer of floor.layers) {
        const found = layer.elements.find(el => el.id === elementId);
        if (found) {
          element = found;
          break;
        }
      }
      if (element) break;
    }

    if (!element) return;

    const currentRotation = element.rotation || 0;
    const newRotation = currentRotation + deltaAngle;

    get().rotateElement(elementId, newRotation);
  },

  // BaseMap actions
  uploadBaseMap: async (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const state = get();
        if (!state.project) {
          reject(new Error('No project loaded'));
          return;
        }

        const imageData = e.target?.result as string;

        // 创建Image对象获取尺寸
        const img = new Image();
        img.onload = () => {
          // 底图初始放置在世界坐标原点，Canvas2D会自动居中显示
          const baseMap: BaseMap = {
            id: uuidv4(),
            fileName: file.name,
            imageData,
            width: img.width,
            height: img.height,
            scale: 100, // 默认100像素=1米
            offset: { x: 0, y: 0 }, // 从原点开始
            rotation: 0,
            opacity: 0.7, // 默认70%透明度便于描绘
            visible: true,
          };

          const updatedProject: Project = {
            ...state.project!,
            mapConfig: {
              ...state.project!.mapConfig,
              baseMap,
            },
            updatedAt: new Date().toISOString(),
          };

          set({ project: updatedProject });
          get().pushHistory();

          // 自动进入校准模式
          get().startCalibration();

          resolve();
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = imageData;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  },

  updateBaseMap: (updates) => {
    const state = get();
    if (!state.project || !state.project.mapConfig.baseMap) return;

    const project = {
      ...state.project,
      mapConfig: {
        ...state.project.mapConfig,
        baseMap: {
          ...state.project.mapConfig.baseMap,
          ...updates,
        },
      },
      updatedAt: new Date().toISOString(),
    };

    set({ project });
    get().pushHistory();
  },

  removeBaseMap: () => {
    const state = get();
    if (!state.project) return;

    const project = {
      ...state.project,
      mapConfig: {
        ...state.project.mapConfig,
        baseMap: undefined,
      },
      updatedAt: new Date().toISOString(),
    };

    set({ project });
    get().pushHistory();
  },

  // Preview actions
  openPreview: () => {
    set({ isPreviewOpen: true });
  },

  closePreview: () => {
    set({ isPreviewOpen: false });
  },

  savePreviewCameraState: (state: CameraState) => {
    set({ previewCameraState: state });
  },

  resetPreviewCamera: () => {
    set({ previewCameraState: null });
  },

  // Wall property actions
  setWallThickness: (thickness: number) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        thickness,
      },
    }));
  },

  setWallHeight: (height: number) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        height,
      },
    }));
  },

  setWallMaterial: (material: string) => {
    // 获取材质的默认配置
    const config = getMaterialConfig(material as WallMaterialType);
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        material,
        thickness: config.thickness,
        color: config.color,
        opacity: config.opacity,
      },
    }));
  },

  setWallColor: (color: string) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        color,
      },
    }));
  },

  setWallOpacity: (opacity: number) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        opacity,
      },
    }));
  },

  setCustomAngle: (angle: number | null) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        customAngle: angle,
      },
    }));
  },

  toggleUseCustomAngle: (use: boolean) => {
    set((state) => ({
      wallProperties: {
        ...state.wallProperties,
        useCustomAngle: use,
      },
    }));
  },

  // Right panel actions
  setRightPanelTab: (tab: RightPanelTab) => {
    set({ rightPanelTab: tab });
  },

  // Point hover and edit actions
  setHoveredPoint: (point: { elementId: string; pointIndex: number } | null) => {
    set({ hoveredPoint: point });
  },

  updateElementPoint: (elementId: string, pointIndex: number, newPoint: Point2D) => {
    const state = get();
    if (!state.project) return;

    const project = { ...state.project };
    let updated = false;

    for (const floor of project.floors) {
      for (const layer of floor.layers) {
        const element = layer.elements.find((el) => el.id === elementId);
        if (element && element.points[pointIndex]) {
          element.points[pointIndex] = newPoint;
          updated = true;
          break;
        }
      }
      if (updated) break;
    }

    if (updated) {
      project.updatedAt = new Date().toISOString();
      set({ project });
      get().pushHistory();
    }
  },

  // Area naming actions
  setPendingArea: (points: Point2D[] | null) => {
    set({ pendingArea: points ? { points } : null });
  },

  createAreaWithName: (name: string) => {
    const state = get();
    if (!state.pendingArea || !state.project) return;

    // 检查是否需要设置有效区域
    const needsValidArea = !state.project.mapConfig.validArea && state.project.mapConfig.baseMap;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const areaElement: AreaElement = {
      id,
      type: 'area',
      name: name || `${getDefaultName('area')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: state.pendingArea.points,
      properties: {},
      color: '#bed1fe', // 全部使用浅蓝色
      fillOpacity: 0.3,
      areaName: name,
      showName: true,
    };

    get().addElement(areaElement);

    // 如果是第一个区域且没有有效区域，则设为有效区域
    if (needsValidArea) {
      set((state) => ({
        project: state.project ? {
          ...state.project,
          mapConfig: {
            ...state.project.mapConfig,
            validArea: state.pendingArea!.points,
          },
        } : null,
      }));
    }

    set({ pendingArea: null, isDrawing: false, tempPoints: [] });
  },

  // Furniture property actions
  setPendingFurniture: (points: Point2D[] | null) => {
    set({ pendingFurniture: points ? { points } : null });
  },

  createFurnitureWithProperties: (data: { name: string; width: number; depth: number; height: number }) => {
    const state = get();
    if (!state.pendingFurniture || !state.project) return;

    const pendingPoints = state.pendingFurniture.points;
    if (pendingPoints.length < 2) return;

    // 使用绝对值计算尺寸，确保无论拖拽方向如何都能正确处理
    const p1 = pendingPoints[0];
    const p2 = pendingPoints[1];
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);
    const minY = Math.min(p1.y, p2.y);
    const maxY = Math.max(p1.y, p2.y);

    // 计算拖拽区域的中心点
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // 用户输入的 width/depth 是厘米值，需要转换为米（世界坐标单位）
    // 确保尺寸有效，最小值为 1 像素（约 1cm）
    const newWidthM = Math.max(0.01, data.width / 100);
    const newDepthM = Math.max(0.01, data.depth / 100);

    // 计算拖拽时的原始尺寸（用于调试对比）
    const dragWidthCm = (maxX - minX) * 100;
    const dragDepthCm = (maxY - minY) * 100;

    console.log(`📐 [createFurniture] 拖拽尺寸: ${dragWidthCm.toFixed(0)}cm x ${dragDepthCm.toFixed(0)}cm`);
    console.log(`📐 [createFurniture] 用户输入尺寸: ${data.width}cm x ${data.depth}cm`);
    console.log(`📐 [createFurniture] 转换后: ${newWidthM.toFixed(4)}m x ${newDepthM.toFixed(4)}m`);
    console.log(`📐 [createFurniture] 中心点: (${centerX.toFixed(2)}, ${centerY.toFixed(2)})`);

    // 计算新的 points（基于中心点，未旋转）
    const halfWidth = newWidthM / 2;
    const halfDepth = newDepthM / 2;

    const newPoints: Point2D[] = [
      { x: centerX - halfWidth, y: centerY - halfDepth },  // 左上
      { x: centerX + halfWidth, y: centerY - halfDepth },  // 右上
      { x: centerX + halfWidth, y: centerY + halfDepth },  // 右下
      { x: centerX - halfWidth, y: centerY + halfDepth },  // 左下
    ];

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const furnitureElement: ShapeElement = {
      id,
      type: 'rectangle',
      name: data.name || `${getDefaultName('furniture')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: newPoints,
      properties: {},
      height: data.height,
      color: '#a5d8ff',
      rotation: 0,
      opacity: 0.9,
      furnitureName: data.name,
      width: data.width,   // 保持厘米值
      depth: data.depth,   // 保持厘米值
    };

    get().addElement(furnitureElement);
    set({ pendingFurniture: null, isDrawing: false, tempPoints: [] });
  },

  // Circle property actions
  setPendingCircle: (points: Point2D[] | null) => {
    set({ pendingCircle: points ? { points } : null });
  },

  createCircleWithProperties: (data: { name: string; radius: number; height: number }) => {
    const state = get();
    if (!state.pendingCircle || !state.project) return;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const circleElement: ShapeElement = {
      id,
      type: 'circle',
      name: data.name || `${getDefaultName('circle')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: state.pendingCircle.points,
      properties: {},
      height: data.height,
      color: '#b8ddf0', // 浅蓝色
      rotation: 0,
      opacity: 0.9, // 透明度
      furnitureName: data.name,
      radius: data.radius,
    };

    get().addElement(circleElement);
    set({ pendingCircle: null, isDrawing: false, tempPoints: [] });
  },

  // Polygon property actions
  setPendingPolygon: (points: Point2D[] | null) => {
    set({ pendingPolygon: points ? { points } : null });
  },

  createPolygonWithProperties: (data: { name: string; height: number }) => {
    const state = get();
    if (!state.pendingPolygon || !state.project) return;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const polygonElement: ShapeElement = {
      id,
      type: 'polygon',
      name: data.name || `${getDefaultName('polygon')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: state.pendingPolygon.points,
      properties: {},
      height: data.height,
      color: '#c3e7f5', // 淡青色
      rotation: 0,
      opacity: 0.9, // 透明度
      furnitureName: data.name,
    };

    get().addElement(polygonElement);
    set({ pendingPolygon: null, isDrawing: false, tempPoints: [] });
  },

  // Door, Window, Stair actions
  setDoorWindowDrawing: (data: { wallId: string; wallSegmentIndex: number; startPoint: Point2D } | null) => {
    set({ doorWindowDrawing: data });
  },

  setPendingDoor: (data: { wallId: string; wallSegmentIndex: number; point1: Point2D; point2: Point2D } | null) => {
    set({ pendingDoor: data, doorWindowDrawing: null });
  },

  createDoorWithProperties: (data: { name?: string; height: number; openAngle: number }) => {
    const state = get();
    if (!state.pendingDoor || !state.project) return;

    // 获取墙面元素
    const floor = state.project.floors.find(f => f.id === state.currentFloor);
    if (!floor) return;

    let wall: WallElement | null = null;
    for (const layer of floor.layers) {
      const foundWall = layer.elements.find(e => e.id === state.pendingDoor!.wallId && e.type === 'wall') as WallElement;
      if (foundWall) {
        wall = foundWall;
        break;
      }
    }
    if (!wall || wall.points.length < 2) return;

    const pending = state.pendingDoor;
    const segmentIndex = pending.wallSegmentIndex;
    const wallP1 = wall.points[segmentIndex];
    const wallP2 = wall.points[segmentIndex + 1];

    // 计算门的宽度（两点之间的距离）
    const doorWidth = Math.sqrt(
      (pending.point2.x - pending.point1.x) ** 2 +
      (pending.point2.y - pending.point1.y) ** 2
    );

    // 计算中心点位置（在墙面线段上的投影参数）
    const dx = wallP2.x - wallP1.x;
    const dy = wallP2.y - wallP1.y;
    const segmentLenSq = dx * dx + dy * dy;
    const centerX = (pending.point1.x + pending.point2.x) / 2;
    const centerY = (pending.point1.y + pending.point2.y) / 2;
    const t = ((centerX - wallP1.x) * dx + (centerY - wallP1.y) * dy) / segmentLenSq;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const doorElement: DoorElement = {
      id,
      type: 'door',
      name: data.name || `${getDefaultName('door')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: [pending.point1, pending.point2],
      properties: {},
      width: doorWidth,
      height: data.height,
      openAngle: data.openAngle,
      wallId: pending.wallId,
      wallSegmentIndex: segmentIndex,
      positionOnWall: Math.max(0, Math.min(1, t)),
    };

    get().addElement(doorElement);
    set({ pendingDoor: null });
  },

  setPendingStair: (points: Point2D[] | null) => {
    set({ pendingStair: points ? { points } : null });
  },

  createStairWithProperties: (data: { width: number; length: number; height: number; steps: number; direction: 'up' | 'down' }) => {
    const state = get();
    if (!state.pendingStair || !state.project) return;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const stairElement: StairElement = {
      id,
      type: 'stair',
      name: `${getDefaultName('stair')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: state.pendingStair.points,
      properties: {},
      width: data.width,
      length: data.length,
      height: data.height,
      steps: data.steps,
      direction: data.direction,
    };

    get().addElement(stairElement);
    set({ pendingStair: null, isDrawing: false, tempPoints: [] });
  },

  // Text tool actions
  setPendingText: (point: Point2D | null) => {
    set({ pendingText: point ? { point } : null });
  },

  createTextWithContent: (data: {
    content: string;
    fontSize?: number;
    color?: string;
    borderColor?: string;
    backgroundColor?: string;
    backgroundOpacity?: number;
  }) => {
    const state = get();
    if (!state.pendingText || !state.project) return;

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;
    const textElement: TextElement = {
      id,
      type: 'text',
      name: `${getDefaultName('text')}-${uuid.substring(0, 8)}`,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points: [state.pendingText.point],
      properties: {},
      elevation: 8500, // 默认离地高度850cm (8500mm)
      rotation: 0,
      content: data.content,
      fontSize: data.fontSize || 16,
      color: data.color || '#000000',
      fontFamily: 'sans-serif',
      bold: false,
      italic: false,
      align: 'center',
      borderColor: data.borderColor || '#ADD8E6',
      backgroundColor: data.backgroundColor || '#FFFFFF',
      backgroundOpacity: data.backgroundOpacity !== undefined ? data.backgroundOpacity : 0.8,
      padding: 4, // 减小内边距
    };

    get().addElement(textElement);
    set({ pendingText: null });
  },

  // Calibration actions
  startCalibration: () => {
    set({
      isCalibrating: true,
      calibrationPoint1: null,
      calibrationPoint2: null,
      currentTool: ToolType.SELECT, // 切换到选择工具
      isDrawing: false,
      tempPoints: [],
    });
  },

  setCalibrationPoint1: (point: Point2D) => {
    set({ calibrationPoint1: point, calibrationPoint2: point });
  },

  updateCalibrationPoint2: (point: Point2D) => {
    const state = get();
    if (state.calibrationPoint1) {
      set({ calibrationPoint2: point });
    }
  },

  finishCalibration: (realLengthCm?: number, imagePixelLength?: number, directModelToRealScale?: number) => {
    const state = get();
    if (!state.project) return;

    const baseMap = state.project.mapConfig.baseMap;
    if (!baseMap) return;

   
    console.log('directModelToRealScale----', directModelToRealScale)
    
    let calibrationScale;
    let pixelLength: number | undefined;

    if (directModelToRealScale == undefined) {
      if (!state.calibrationPoint1 || !state.calibrationPoint2) return;
      if (!baseMap) return;
      if (realLengthCm == undefined) return;

      // 使用传入的底图像素长度，如果没有则回退到屏幕像素计算（向后兼容）
      if (imagePixelLength !== undefined && imagePixelLength > 0) {
        pixelLength = imagePixelLength;
        console.log('📏 使用底图实际像素长度:', pixelLength.toFixed(2), 'px');
      } else {
        // 回退：计算屏幕像素长度（不推荐，仅用于向后兼容）
        const p1 = state.calibrationPoint1;
        const p2 = state.calibrationPoint2;
        pixelLength = Math.sqrt(
          Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
        );
        console.warn('⚠️ 使用屏幕像素长度（不准确）:', pixelLength.toFixed(2), 'px');
      }

      if (pixelLength === 0) return;

      // 计算比例：像素/厘米
      calibrationScale = pixelLength / realLengthCm;
    } else {
      console.log('自动填入')
      calibrationScale = directModelToRealScale / 100

    }

    console.log('calibrationScale----', calibrationScale)

    // 计算整张底图的真实尺寸（厘米）
    const realWidth = baseMap.width / calibrationScale;
    const realHeight = baseMap.height / calibrationScale;

    // 计算3D模型坐标与真实世界坐标的比例
    // 编辑器坐标（像素1:1映射）→ OBJ导出时/100 → 3D模型坐标（米）
    // 真实世界坐标（cm）→ /100 → 真实世界坐标（米）
    // 比例 = (baseMap.width / 100) / (realWidth / 100) = baseMap.width / realWidth
    const modelToRealScale = baseMap.width / realWidth;

    // 更新项目配置
    // 注意：只保存校准数据，不更新 maxX/maxY
    // 模型边界和隐藏地面平面仍由实际元素范围决定
    // SDK 使用时通过 calibrationScale、realWidth/Height 自行做坐标映射

    // 确保底图的 offset 被设置为 (0, 0)，避免底图贴在左上角
    const updatedBaseMap = {
      ...baseMap,
      offset: { x: 0, y: 0 },  // 重置 offset，确保底图从原点开始
    };

    const project = {
      ...state.project,
      mapConfig: {
        ...state.project.mapConfig,
        baseMap: updatedBaseMap,
        calibrationScale,
        realWidth,
        realHeight,
        isCalibrated: true,
        modelToRealScale,  // 记录3D模型与真实世界的比例
      },
      updatedAt: new Date().toISOString(),
    };

    set({
      project,
      isCalibrating: false,
      calibrationPoint1: null,
      calibrationPoint2: null
    });
    get().pushHistory();

    // 自动创建一个覆盖整张底图的透明区域（用于定义坐标范围）
    const boundaryAreaPoints: Point2D[] = [
      { x: 0, y: 0 },                          // 左上角
      { x: baseMap.width, y: 0 },              // 右上角
      { x: baseMap.width, y: baseMap.height }, // 右下角
      { x: 0, y: baseMap.height },             // 左下角
    ];

    console.log('自动创建一个覆盖整张底图的透明区域（用于定义坐标范围）----', boundaryAreaPoints)

    const uuid = uuidv4().replace(/-/g, '');
    const boundaryArea: AreaElement = {
      id: `kimap${uuid}`,
      type: 'area',
      name: '__boundary_area__',
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: true,  // 锁定，防止误删
      points: boundaryAreaPoints,
      properties: {
        isCalibrationBoundary: true,  // 标记为校准边界
      },
      color: '#000000',       // 黑色
      fillOpacity: 0,         // 完全透明，SDK渲染时不可见
    };

    // 添加边界区域到当前图层
    get().addElement(boundaryArea);

    console.log('✅ 地图校准完成：');
    if (directModelToRealScale == undefined) {
      console.log(`  - 测量像素长度: ${pixelLength && pixelLength.toFixed(2)} px`);
      console.log(`  - 真实长度: ${realLengthCm} cm`);
    }
    console.log(`  - 像素比例: ${calibrationScale.toFixed(4)} px/cm`);
    console.log(`  - 底图真实尺寸: ${realWidth.toFixed(2)} cm × ${realHeight.toFixed(2)} cm`);
    console.log(`  - 3D模型边界: ${(baseMap.width / 100).toFixed(2)} m × ${(baseMap.height / 100).toFixed(2)} m`);
    console.log(`  - 3D/真实世界比例: ${modelToRealScale.toFixed(4)} (3D模型${modelToRealScale.toFixed(2)}倍于真实)`);
    console.log(`  - 已创建底图边界区域（完全透明）`);
  },

  cancelCalibration: () => {
    const state = get();

    // 如果取消校准，删除底图（强制用户必须校准）
    if (state.project?.mapConfig?.baseMap && !state.project.mapConfig.isCalibrated) {
      console.log('⚠️ 取消校准，删除底图');
      get().removeBaseMap();
    }

    set({
      isCalibrating: false,
      calibrationPoint1: null,
      calibrationPoint2: null
    });
  },

  // 自动校准：根据已知比例直接计算
  // scale: 真实世界1米对应的图片像素数（如 58.57 表示 1米 = 58.57像素）
  autoCalibrate: (scale: number) => {
    const state = get();
    if (!state.project) return;

    const baseMap = state.project.mapConfig.baseMap;
    if (!baseMap) return;

    console.log('🔧 开始自动校准，原始scale:', scale);

    // scale=58.57 表示 1米 = 58.57像素
    // calibrationScale = px/cm = scale / 100
    const calibrationScale = scale / 100; // 0.5857 px/cm

    // 计算整张底图的真实尺寸（厘米）
    const realWidth = baseMap.width / calibrationScale;
    const realHeight = baseMap.height / calibrationScale;

    // 计算3D模型坐标与真实世界坐标的比例
    const modelToRealScale = baseMap.width / realWidth;

    // 确保底图的 offset 被设置为 (0, 0)，并保存 scale
    // 注意：这里创建新的 baseMap 对象，保留了原有的 id 和其他属性
    const updatedBaseMap = {
      ...baseMap,
      offset: { x: 0, y: 0 },
      scale: scale,  // 保存 scale，用于 Canvas2D 计算初始缩放
    };

    // 打印调试信息
    console.log('🔧 autoCalibrate 详细参数:', {
      inputScale: scale,
      calibrationScale: (scale / 100).toFixed(4),
      baseMapWidth: baseMap.width,
      baseMapHeight: baseMap.height,
      worldWidth: (baseMap.width * (100 / scale)).toFixed(2),
      worldHeight: (baseMap.height * (100 / scale)).toFixed(2),
    });

    // 计算底图在世界坐标系中的尺寸（与 BaseMapRenderer 保持一致）
    // worldWidth = 像素 * (100 / scale) = 像素 / (scale / 100) = 像素 / calibrationScale
    const worldWidth = baseMap.width * (100 / scale);
    const worldHeight = baseMap.height * (100 / scale);

    const project = {
      ...state.project,
      mapConfig: {
        ...state.project.mapConfig,
        baseMap: updatedBaseMap,
        calibrationScale,
        realWidth,
        realHeight,
        isCalibrated: true,
        autoCalibrated: true,
        modelToRealScale,
      },
      updatedAt: new Date().toISOString(),
    };

    set({
      project,
      isCalibrating: false,
      calibrationPoint1: null,
      calibrationPoint2: null
    });
    get().pushHistory();

    // 自动创建一个覆盖整张底图的透明区域（用于定义坐标范围）
    // 使用世界坐标（米），与底图渲染一致
    const boundaryAreaPoints: Point2D[] = [
      { x: 0, y: 0 },
      { x: worldWidth, y: 0 },
      { x: worldWidth, y: worldHeight },
      { x: 0, y: worldHeight },
    ];

    const uuid = uuidv4().replace(/-/g, '');
    const boundaryArea: AreaElement = {
      id: `kimap${uuid}`,
      type: 'area',
      name: '__boundary_area__',
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: true,
      points: boundaryAreaPoints,
      properties: {
        isCalibrationBoundary: true,
      },
      color: '#000000',
      fillOpacity: 0,
    };

    get().addElement(boundaryArea);

    console.log('✅ 自动校准完成：');
    console.log(`  - 原始scale: ${scale} px/m`);
    console.log(`  - 像素比例: ${calibrationScale.toFixed(4)} px/cm`);
    console.log(`  - 底图尺寸: ${baseMap.width} x ${baseMap.height} px`);
    console.log(`  - 世界尺寸: ${worldWidth.toFixed(2)} x ${worldHeight.toFixed(2)} (1世界单位=1cm)`);
    console.log(`  - 真实尺寸: ${realWidth.toFixed(2)} cm x ${realHeight.toFixed(2)} cm`);
    console.log(`  - 真实尺寸: ${(realWidth / 100).toFixed(2)} m x ${(realHeight / 100).toFixed(2)} m`);
    console.log(`  - 已创建底图边界区域（完全透明）`);
  },

  // 3D Model placement actions
  startModel3DPlacement: (model: Model3DPlacementState) => {
    set({
      model3DPlacement: model,
      currentTool: ToolType.MODEL_3D,
      isDrawing: false,
      tempPoints: [],
    });
  },

  cancelModel3DPlacement: () => {
    set({
      model3DPlacement: null,
      currentTool: ToolType.SELECT,
    });
  },

  placeModel3D: (position: Point2D, outlineData?: { lines: { x: number; y: number }[][]; width: number; height: number }) => {
    const state = get();
    if (!state.model3DPlacement || !state.project) return;

    const model = state.model3DPlacement;
    const halfWidth = model.width / 2;
    const halfDepth = model.depth / 2;

    console.log('📍 放置3D模型:', {
      name: model.modelName,
      position: { x: position.x.toFixed(2), y: position.y.toFixed(2), unit: 'px' },
      canvasSize: { width: model.width.toFixed(2), depth: model.depth.toFixed(2), unit: 'px' },
      realSize: { width: model.realWidth, depth: model.realDepth, height: model.height, unit: 'mm' },
      threeDSize: { width: (model.width / 100).toFixed(2), depth: (model.depth / 100).toFixed(2), unit: 'm' },
      hasOutline: !!outlineData
    });

    // 创建矩形的四个角点（以position为中心）
    const points: Point2D[] = [
      { x: position.x - halfWidth, y: position.y - halfDepth },
      { x: position.x + halfWidth, y: position.y - halfDepth },
      { x: position.x + halfWidth, y: position.y + halfDepth },
      { x: position.x - halfWidth, y: position.y + halfDepth },
    ];

    // 生成以"kimap"开头的唯一ID
    const uuid = uuidv4().replace(/-/g, '');
    const id = `kimap${uuid}`;

    const model3DElement: Model3DElement = {
      id,
      type: 'model3d',
      name: model.modelName,
      layerId: state.currentLayer,
      floorId: state.currentFloor,
      visible: true,
      locked: false,
      points,
      properties: {},
      modelPath: model.modelPath,
      modelName: model.modelName,
      modelCategory: model.modelCategory, // 保存分类信息
      width: model.width,           // Canvas pixels
      depth: model.depth,           // Canvas pixels
      height: model.height,         // Real mm
      realWidth: model.realWidth,   // Real mm
      realDepth: model.realDepth,   // Real mm
      scale: 1,
      color: '#93c5fd', // 浅蓝色
      isBuiltIn: model.isBuiltIn,
      rotation: 0,
      outlineData, // 保存轮廓数据
    };

    get().addElement(model3DElement);

    // 放置后退出放置模式，并自动选中新添加的家具
    set({
      model3DPlacement: null,
      currentTool: ToolType.SELECT,
      selectedElements: [id],  // 自动选中新添加的家具
      rightPanelTab: 'elements',  // 切换到元素属性面板
    });
  },

  // Category visibility actions
  setHiddenCategories: (categories: Record<string, boolean>) => {
    set({ hiddenCategories: categories });
  },

  toggleCategoryVisibility: (category: string) => {
    set((state) => ({
      hiddenCategories: {
        ...state.hiddenCategories,
        [category]: !state.hiddenCategories[category],
      },
    }));
  },

  // Measurement tool actions
  startMeasurement: () => {
    set({
      isMeasuring: true,
      currentMeasurement: [],
      measurementPreviewPoint: null,
    });
  },

  addMeasurementPoint: (point: Point2D) => {
    const state = get();
    if (!state.isMeasuring) return;

    const currentMeasurement = state.currentMeasurement || [];
    set({
      currentMeasurement: [...currentMeasurement, point],
    });
  },

  updateMeasurementPreview: (point: Point2D | null) => {
    set({ measurementPreviewPoint: point });
  },

  completeMeasurement: () => {
    const state = get();
    if (!state.currentMeasurement || state.currentMeasurement.length < 2) {
      set({ isMeasuring: false, currentMeasurement: null, measurementPreviewPoint: null });
      return;
    }

    const project = state.project;
    if (!project) return;

    const calibrationScale = project.mapConfig?.calibrationScale || 10; // 默认10px/cm
    const isAutoCalibrated = project.mapConfig?.autoCalibrated || false;

    // 计算每段长度和总长度
    const points = state.currentMeasurement;
    const segmentLengths: number[] = [];
    let totalLength = 0;

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const pixelLength = Math.sqrt(
        Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
      );
      // 如果是自动校准，世界坐标直接就是厘米，不需要再除以 calibrationScale
      const realLength = isAutoCalibrated ? pixelLength : pixelLength / calibrationScale;
      segmentLengths.push(realLength);
      totalLength += realLength;
    }

    const newMeasurement: MeasurementLine = {
      id: uuidv4(),
      points: [...points],
      isComplete: true,
      totalLength,
      segmentLengths,
      createdAt: new Date().toISOString(),
    };

    set({
      completedMeasurements: [...state.completedMeasurements, newMeasurement],
      isMeasuring: false,
      currentMeasurement: null,
      measurementPreviewPoint: null,
    });
  },

  cancelMeasurement: () => {
    set({
      isMeasuring: false,
      currentMeasurement: null,
      measurementPreviewPoint: null,
    });
  },

  deleteMeasurement: (id: string) => {
    const state = get();
    set({
      completedMeasurements: state.completedMeasurements.filter(m => m.id !== id),
    });
  },

  clearAllMeasurements: () => {
    set({
      completedMeasurements: [],
      isMeasuring: false,
      currentMeasurement: null,
      measurementPreviewPoint: null,
    });
  },

  // External API actions
  setApiUrl: (apiUrl) => set({ apiUrl }),
}));

// Helper function to create element from points
function createElementFromPoints(
  tool: ToolType,
  points: Point2D[],
  layerId: string,
  floorId: string,
  wallProperties: { thickness: number; height: number; material: string; color: string; opacity: number }
): Element | null {
  // 生成以"kimap"开头的唯一ID
  const uuid = uuidv4().replace(/-/g, '');
  const id = `kimap${uuid}`;
  const baseElement = {
    id,
    name: `${getDefaultName('wall')}-${uuid.substring(0, 8)}`,
    layerId,
    floorId,
    visible: true,
    locked: false,
    points,
    properties: {},
  };

  switch (tool) {
    case ToolType.WALL: {
      const wallElement: WallElement = {
        ...baseElement,
        type: 'wall',
        thickness: wallProperties.thickness,
        height: wallProperties.height, // 3D渲染高度（逻辑上代表3000mm即3米）
        material: wallProperties.material,
        color: wallProperties.color || '#d1d5db', // 使用用户选择的颜色
        opacity: wallProperties.opacity ?? 1,       // 使用用户选择的透明度
      };
      return wallElement;
    }

    case ToolType.AREA: {
      const areaElement: AreaElement = {
        ...baseElement,
        name: `${getDefaultName('area')}-${uuid.substring(0, 8)}`,
        type: 'area',
        color: '#bed1fe', // 淡蓝色
        fillOpacity: 0.3,
      };
      return areaElement;
    }

    case ToolType.RECTANGLE: {
      const rectElement: ShapeElement = {
        ...baseElement,
        name: `${getDefaultName('furniture')}-${uuid.substring(0, 8)}`,
        type: 'rectangle',
        height: 750, // 真实桌子高度750mm（75cm）
        color: '#a5d8ff', // 淡蓝色
        rotation: 0,
        opacity: 0.9, // 透明度
      };
      return rectElement;
    }

    case ToolType.CIRCLE: {
      const circleElement: ShapeElement = {
        ...baseElement,
        name: `${getDefaultName('circle')}-${uuid.substring(0, 8)}`,
        type: 'circle',
        height: 750, // 真实设备高度750mm
        color: '#b8ddf0', // 浅蓝色
        rotation: 0,
        opacity: 0.9, // 透明度
      };
      return circleElement;
    }

    case ToolType.POLYGON: {
      const polygonElement: ShapeElement = {
        ...baseElement,
        name: `${getDefaultName('polygon')}-${uuid.substring(0, 8)}`,
        type: 'polygon',
        height: 750, // 真实物体高度750mm
        color: '#c3e7f5', // 淡青色
        rotation: 0,
        opacity: 0.9, // 透明度
      };
      return polygonElement;
    }

    default:
      return null;
  }
}
